import React, { useMemo, useState } from 'react';
import {
  CONVERSATION_CREATED_EVENT,
  CONVERSATION_TITLE_GENERATED_EVENT,
  CONVERSATION_TITLE_UPDATED_EVENT,
  CONVERSATION_DELETED_EVENT,
  useChatWidgetContext,
  ConfirmDeletePopup,
  usePopupFormManager,
} from 'features/nekst-widgets';
import { usePopupManager, FormTemplate, TextInput } from 'ui-builder';
import { useDataLoaderNew } from 'data-loader';
import { useEventsSubscriber, useEventsPublisher } from 'event-bus';
import {
  LlmConversation,
  useObjectDatesMapper,
  useLlmConversationsService,
} from 'nekst-api';
import useChatsNavigator from './chatsNavigator';
import sidebarStyles from './ConversationsSidebar.module.scss';

const PAGE_SIZE = 20;

function getDateGroupLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const target = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  if (target.getTime() >= today.getTime()) {
    return 'Today';
  } else if (target.getTime() >= yesterday.getTime()) {
    return 'Yesterday';
  } else if (target.getTime() >= weekAgo.getTime()) {
    return 'Last 7 days';
  } else {
    return 'Older';
  }
}

function getConversationDate(conversation: LlmConversation): Date {
  return conversation.lastMessageAt ?? conversation.createdAt;
}

function getConversationDisplayTitle(conversation: LlmConversation): string {
  if (conversation.title) {
    return conversation.title;
  } else {
    return conversation.createdAt.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}

function groupConversationsByDate(
  conversations: LlmConversation[],
): { label: string; conversations: LlmConversation[] }[] {
  const groups: Record<string, LlmConversation[]> = {};
  const groupOrder: string[] = [];

  conversations.forEach((conv) => {
    const label = getDateGroupLabel(getConversationDate(conv));
    if (!groups[label]) {
      groups[label] = [];
      groupOrder.push(label);
    }
    groups[label].push(conv);
  });

  return groupOrder.map((label) => ({
    label,
    conversations: groups[label],
  }));
}

function RenameConversationForm(props: {
  conversationId: number;
  currentTitle: string;
  afterSubmitFunc?: () => Promise<void>;
}) {
  const conversationsService = useLlmConversationsService();
  const eventsPublisher = useEventsPublisher();

  return (
    <FormTemplate<{ title: string }>
      title="Rename Conversation"
      submitFormFunc={async (data) => {
        await conversationsService.updateConversation(props.conversationId, { title: data.title });
        eventsPublisher.publish(CONVERSATION_TITLE_UPDATED_EVENT, {
          conversationId: props.conversationId,
          title: data.title,
        });
        return data;
      }}
      initialData={{ title: props.currentTitle }}
      validationSchema={{
        title: {
          type: 'string',
          constraints: {
            required: true,
            minLength: 1,
            maxLength: 255,
          },
        },
      }}
      afterSubmitFunc={props.afterSubmitFunc}
    >
      <TextInput source="title" label="Title" />
    </FormTemplate>
  );
}

function RenameConversationButton(props: {
  conversation: LlmConversation;
}) {
  const popupFormManager = usePopupFormManager();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    popupFormManager.openForm(
      <RenameConversationForm
        conversationId={props.conversation.id}
        currentTitle={props.conversation.title || ''}
      />,
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-center justify-center w-6 h-6 rounded bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-700"
      data-testid="conversation-rename-button"
      title="Rename conversation"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );
}

function DeleteConversationButton(props: {
  conversation: LlmConversation;
}) {
  const conversationsService = useLlmConversationsService();
  const { resetConversation, currentConversationId } = useChatWidgetContext();
  const { openChats } = useChatsNavigator();
  const popupManager = usePopupManager();
  const eventsPublisher = useEventsPublisher();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    popupManager.openPopup(
      <ConfirmDeletePopup
        deleteFunc={async () => {
          await conversationsService.deleteConversation(props.conversation.id);
          eventsPublisher.publish(CONVERSATION_DELETED_EVENT, { conversationId: props.conversation.id });
          if (currentConversationId === props.conversation.id) {
            resetConversation();
            openChats();
          }
        }}
        message={`Are you sure you want to delete this conversation${props.conversation.title ? ` "${props.conversation.title}"` : ''}?`}
      />,
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-center justify-center w-6 h-6 rounded bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500"
      data-testid="conversation-delete-button"
      title="Delete conversation"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    </button>
  );
}

function ConversationItem(props: {
  conversation: LlmConversation;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={`group relative flex items-center rounded-lg ${
        props.active ? 'bg-gray-200' : 'hover:bg-gray-100'
      }`}
      data-testid="conversation-item"
    >
      <button
        type="button"
        onClick={props.onClick}
        className="block w-full text-left px-3 py-2.5 border-none cursor-pointer text-sm leading-[1.4] text-gray-800 bg-transparent min-w-0"
      >
        <div className="truncate pr-6">
          {getConversationDisplayTitle(props.conversation)}
        </div>
        {props.conversation.lastMessagePreview && (
          <div className="text-xs text-gray-500 truncate mt-0.5 pr-6">
            {props.conversation.lastMessagePreview}
          </div>
        )}
      </button>
      <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex group-focus-within:flex items-center gap-0.5">
        <RenameConversationButton conversation={props.conversation} />
        <DeleteConversationButton conversation={props.conversation} />
      </div>
    </div>
  );
}

function DateGroup(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-gray-500 px-3 pt-2 pb-1 uppercase tracking-[0.03em]">
        {props.label}
      </div>
      <div className="flex flex-col gap-0.5">
        {props.children}
      </div>
    </div>
  );
}

function LoadMoreButton(props: { onClick: () => void; loading: boolean }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.loading}
      className={`block w-full px-3 py-2.5 border-none bg-transparent text-gray-500 text-[13px] text-center ${
        props.loading ? 'cursor-default' : 'cursor-pointer'
      }`}
      data-testid="load-more-button"
    >
      {props.loading ? 'Loading...' : 'Load more'}
    </button>
  );
}

export default function ConversationsSidebar() {
  const { resetConversation, currentConversationId } = useChatWidgetContext();
  const conversationsService = useLlmConversationsService();
  const { openChat, openChats } = useChatsNavigator();

  const conversationMapper = useObjectDatesMapper<LlmConversation>([], ['createdAt', 'updatedAt', 'lastMessageAt']);

  const [conversations, setConversations] = useState<LlmConversation[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const { loading } = useDataLoaderNew(
    async () => {
      const result = await conversationsService.getConversations({
        limit: PAGE_SIZE,
        offset: 0,
      });
      setConversations(result.data);
      setTotal(result.total);
      return result;
    },
    { dependencies: [] },
  );

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const result = await conversationsService.getConversations({
        limit: PAGE_SIZE,
        offset: conversations.length,
      });
      setConversations((prev) => [...prev, ...result.data]);
      setTotal(result.total);
    } finally {
      setLoadingMore(false);
    }
  };

  useEventsSubscriber(
    'ConversationsSidebar',
    {
      [CONVERSATION_CREATED_EVENT]: (event) => {
        const conversation = conversationMapper.mapSingle(event.conversation as Record<string, unknown>);
        setConversations((prev) => {
          if (prev.some((c) => c.id === conversation.id)) {
            return prev;
          }
          return [conversation, ...prev];
        });
        setTotal((prev) => prev + 1);
      },
      [CONVERSATION_TITLE_GENERATED_EVENT]: (event) => {
        const { conversationId, title } = event as { conversationId: number; title: string };
        setConversations((prev) => prev.map((conv) => {
          if (conv.id === conversationId) {
            return { ...conv, title };
          }
          return conv;
        }));
      },
      [CONVERSATION_TITLE_UPDATED_EVENT]: (event) => {
        const { conversationId, title } = event as { conversationId: number; title: string };
        setConversations((prev) => prev.map((conv) => {
          if (conv.id === conversationId) {
            return { ...conv, title };
          }
          return conv;
        }));
      },
      [CONVERSATION_DELETED_EVENT]: (event) => {
        const { conversationId } = event as { conversationId: number };
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        setTotal((prev) => prev - 1);
      },
    },
  );

  const hasMore = conversations.length < total;
  const grouped = useMemo(
    () => groupConversationsByDate(conversations),
    [conversations],
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="px-3 py-5 text-gray-400 text-sm text-center">
          Loading conversations...
        </div>
      );
    } else if (conversations.length === 0) {
      return (
        <div
          className="px-3 py-5 text-gray-400 text-sm text-center"
          data-testid="empty-conversations"
        >
          No conversations yet
        </div>
      );
    } else {
      return (
        <>
          {grouped.map((group) => (
            <DateGroup key={group.label} label={group.label}>
              {group.conversations.map((conv) => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  active={currentConversationId === conv.id}
                  onClick={() => openChat(conv.id)}
                />
              ))}
            </DateGroup>
          ))}
          {hasMore && (
            <LoadMoreButton onClick={handleLoadMore} loading={loadingMore} />
          )}
        </>
      );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-3 py-2.5 bg-transparent text-gray-800 rounded-lg font-medium text-sm border border-gray-200 cursor-pointer shadow-sm transition-colors duration-150 hover:bg-gray-100 hover:border-gray-300 active:bg-gray-200"
          onClick={() => { resetConversation(); openChats(); }}
          data-testid="new-chat-button"
        >
          <span className="text-base leading-none">+</span>
          New Thread
        </button>
      </div>
      <div className={`flex-1 overflow-y-auto px-3 ${sidebarStyles.scrollArea}`}>
        {renderContent()}
      </div>
    </div>
  );
}
